import { useEffect, useRef, useState } from "react";
import { customerApi } from "../../lib/customerAuth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PaperPlaneRight, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatErr } from "../../lib/api";
import { openChatSocket } from "../../lib/chatSocket";

/**
 * Sprint G · Batch 2 — Panel de chat del cliente final con WebSocket en tiempo real.
 * Carga el histórico vía REST y suscribe a /api/chats/ws para nuevos mensajes.
 * Fallback automático a polling de 8s si la WS se queda en estado cerrado por
 * mucho tiempo (gestionado por reconnect interno del helper).
 */
export default function CustomerChatPanel() {
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [wsOpen, setWsOpen] = useState(false);
  const scrollerRef = useRef(null);
  const socketRef = useRef(null);

  useEffect(() => {
    customerApi.get("/customer/organizations").then(({ data }) => {
      setOrgs(data || []);
      if (data?.length && !selectedOrgId) setSelectedOrgId(data[0].id);
    });
  // eslint-disable-next-line
  }, []);

  const scrollToBottom = () => {
    setTimeout(() => {
      scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
    }, 50);
  };

  const loadHistory = async (orgId) => {
    if (!orgId) return;
    try {
      const { data } = await customerApi.get(`/chats/history-as-customer?organization_id=${orgId}`);
      setMessages(data || []);
      scrollToBottom();
    } catch {}
  };

  // Carga histórica + apertura de WebSocket por org seleccionada
  useEffect(() => {
    if (!selectedOrgId) return;
    loadHistory(selectedOrgId);

    // Cerramos socket previo
    if (socketRef.current) { socketRef.current.close(); socketRef.current = null; }
    const conn = openChatSocket({
      kind: "customer",
      organizationId: selectedOrgId,
      onOpen: () => setWsOpen(true),
      onClose: () => setWsOpen(false),
      onMessage: (msg) => {
        if (msg?.type === "message" && msg.data) {
          setMessages((arr) => {
            // dedupe por id
            if (arr.some((m) => m.id === msg.data.id)) return arr;
            return [...arr, msg.data];
          });
          scrollToBottom();
        } else if (msg?.type === "ack" && msg.data) {
          setMessages((arr) => {
            if (arr.some((m) => m.id === msg.data.id)) return arr;
            return [...arr, msg.data];
          });
          scrollToBottom();
        }
      },
    });
    socketRef.current = conn;
    return () => { conn.close(); socketRef.current = null; };
  // eslint-disable-next-line
  }, [selectedOrgId]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!text.trim() || !selectedOrgId) return;
    const body = text.trim();
    setSending(true);
    try {
      const conn = socketRef.current;
      if (conn && conn.isOpen()) {
        // Por WS: optimistic UI con ack pendiente
        conn.send({ message_text: body, client_id: `c-${Date.now()}` });
        setText("");
      } else {
        // Fallback REST si la WS no está abierta
        const { data } = await customerApi.post("/chats/send-from-customer", {
          organization_id: selectedOrgId, message_text: body,
        });
        setMessages((arr) => (arr.some((m) => m.id === data.message?.id) ? arr : [...arr, data.message]));
        setText("");
        scrollToBottom();
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  if (orgs === null) return <div className="text-center text-sm text-gray-500 py-12">Cargando…</div>;
  if (!orgs.length) {
    return (
      <div className="text-center bg-white border border-dashed border-gray-200 rounded-md py-10 px-4 text-sm text-gray-500" data-testid="chats-empty">
        <ChatCircleDots size={32} weight="duotone" className="mx-auto mb-2 text-[var(--ag-text-dim)]" />
        Aún no has interactuado con ningún taller. Reserva una cita y desbloquea el chat.
      </div>
    );
  }
  const selected = orgs.find((o) => o.id === selectedOrgId);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 h-[560px]" data-testid="customer-chat">
      {/* Sidebar talleres */}
      <div className="bg-white border border-gray-200 rounded-md overflow-y-auto" data-testid="chat-orgs-list">
        {orgs.map((o) => (
          <button key={o.id} onClick={()=>setSelectedOrgId(o.id)}
            className={`w-full text-left p-3 border-b border-gray-100 flex items-center gap-2 hover:bg-gray-50 ${selectedOrgId===o.id ? "bg-blue-50/60" : ""}`}
            data-testid={`chat-org-${o.id}`}>
            {o.logo_url ? (
              <img src={o.logo_url} alt="" className="w-9 h-9 rounded-sm object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-sm bg-gray-100 grid place-items-center text-xs"
                style={{ background: o.brand_color || "#0033A0", color: "white" }}>{o.name?.[0] || "?"}</div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm truncate text-[#1A1A1A]">{o.name}</div>
              <div className="text-[10px] text-[var(--ag-text-muted)]">{o.city || ""}</div>
            </div>
          </button>
        ))}
      </div>
      {/* Conversation */}
      <div className="md:col-span-2 bg-white border border-gray-200 rounded-md flex flex-col">
        <div className="p-3 border-b border-gray-200 text-sm font-semibold text-[#1A1A1A] flex items-center justify-between" data-testid="chat-header">
          <span>{selected ? selected.name : "Conversación"}</span>
          <span className={`text-[10px] uppercase tracking-wider ${wsOpen ? "text-emerald-600" : "text-[var(--ag-text-muted)]"}`} data-testid="chat-ws-status">
            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${wsOpen ? "bg-emerald-500" : "bg-gray-300"}`} />
            {wsOpen ? "En vivo" : "Conectando…"}
          </span>
        </div>
        <div ref={scrollerRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="text-center text-xs text-gray-400 py-8">Sin mensajes. Inicia la conversación abajo.</div>
          )}
          {messages.map((m) => {
            const mine = m.sender_type === "customer";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                <div className={`max-w-[80%] rounded-md px-3 py-2 text-sm ${mine ? "bg-[var(--ag-gold)] text-white" : "bg-white border border-gray-200 text-[#1A1A1A]"}`}>
                  {m.message_text}
                  <div className={`text-[9px] mt-1 ${mine ? "text-blue-100" : "text-gray-400"}`}>
                    {new Date(m.created_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <form onSubmit={send} className="p-2 border-t border-gray-200 flex gap-2" data-testid="chat-input-form">
          <Input value={text} onChange={(e)=>setText(e.target.value)}
            placeholder="Escribe tu mensaje al taller…"
            className="rounded-sm" disabled={sending} data-testid="chat-input" />
          <Button type="submit" disabled={sending || !text.trim()}
            className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="chat-send">
            <PaperPlaneRight size={14} weight="fill" />
          </Button>
        </form>
      </div>
    </div>
  );
}
